import Crud from "../Crud";
import MUIBlueColor from "@mui/material/colors/blue";

const matchesSetFilterValue = (filter, value) => {
    const values = filter?.properties?.value || [];
    const operator = filter?.properties?.operator === "not in" ? "not in" : "in";
    const inSet = values.includes(value);

    return operator === "not in" ? !inSet : inSet;
};

/**
 * Static helper functions related to Queue Routes.
 */
export default class Queue extends Crud {

    /**
     * @param {object} queue The queue object.
     * @returns The display name of the queue.
     */
    static getDisplayName(queue) {
        return queue.name;
    }

    /**
     * Determines if a queue matches a search query.
     *
     * @param {object} queue The queue to test.
     * @param {string} query The search query.
     * @returns {boolean} Whether or not the queue matches the query.
     */
    static match(queue, query) {
        return (
            (queue.name.toLowerCase().includes(query)) ||
            queue.id === +query
        );
    }

    /**
     * @param {array} queues The list to sort.
     * @returns {array} The sorted list.
     */
    static sort(queues) {
        return queues.sort((a, b) => {
            if (a.ordinal !== null && b.ordinal !== null) {
                return a.ordinal - b.ordinal;
            } else {
                return (a.ordinal === null) - (b.ordinal === null) || (a.name).localeCompare(b.name);
            }
        });
    }

    /**
     * @returns {string} The color of this object.
     */
    static getBaseColor() {
        return MUIBlueColor;
    }

    /**
     * @returns {number} The base color shade of this object.
     */
    static getBaseColorShade() {
        return 800;
    }

    /**
     * @returns {string} The icon name for this object.
     */
    static getIconName() {
        return "CallSplit";
    }

    /**
     * @param {object} queue The queue
     * @param {string} mode Either verbose or simple, defaults to verbose.
     * @returns A nice string describing the OOB rule for this queue.
     */
    static getOutOfBoundsString(queue, mode = "verbose") {
        const thresholds = [];

        if (queue && queue.thresholdSize !== undefined && queue.thresholdSize !== null) {
            thresholds.push(`${mode === "verbose" ? "size exceeds" : ""} ${queue.thresholdSize}`);
        }
        if (queue && queue.thresholdAgeHours !== undefined && queue.thresholdAgeHours !== null) {
            const business = queue.thresholdAgeHoursIsBusiness === true ? ' business' : '';
            if (queue.thresholdAgeHours > 8) {
                const thresholdAgeDays = Math.round(10 * queue.thresholdAgeHours / (queue.thresholdAgeHoursIsBusiness === true ? 8 : 24)) / 10;
                thresholds.push(`${mode === "verbose" ? "age greater than" : ""} ${thresholdAgeDays}${business} day${thresholdAgeDays === 1 ? '' : 's'}`);
            } else {
                thresholds.push(`${mode === "verbose" ? "age greater than" : ""} ${queue.thresholdAgeHours}${business} hour${queue.thresholdAgeHours === 1 ? '' : 's'}`);
            }
        }

        if (thresholds.length > 0) {
            return thresholds.join(' or ');
        } else {
            return null;
        }
    }

    /**
     * Checks the list of queues to determine which ones include the
     * queueRoute's destination. It checks to ensure queue.table starts with
     * "order_activity", the queue is assigned to a team (via
     * teamQueue.queueId), and the queue filter (ex:
     * {"order_activity.order_status_reason_id":{"type":"set","properties":{"operator":"in","value":[57]}}}) contains only a single
     * filter for order_activity.order_status_reason_id and the
     * queueRoute.toOrderStatusReasonId is in the list.
     *
     * @param {array} queues List of queues.
     * @param {array} teamQueues List of teamQueues.
     * @param {object} queueRoute The queueRoute.
     *
     * @return {array} Array of matching queues. Returns an empty array if no
     * matching queues exist.
     */
    static getMatchingQueuesForQueueRoute(queues, teamQueues, orderActivityTypes, queueRoute) {
        if (!queueRoute?.toOrderStatusReasonId) {
            return [];
        }

        return queues.filter(queue => {
            // if this queue is not an order activity queue, disqualify it. This
            // only works for orders and tickets.
            if (queue.table.startsWith("order_activity") === false) {
                return false;
            }

            // If this queue is not assigned to a team, disqualify it. That
            // means nobody is working it.
            const teamQueueExists = teamQueues.some(teamQueue => teamQueue.queueId === queue.id);
            if (!teamQueueExists) {
                return false;
            }

            // If this queue does not have an order status reason filter,
            // disqualify it.
            if (
                !queue.filter ||
                !queue.filter["order_activity.order_status_reason_id"]
            ) {
                return false;
            }

            // TODO: This will potentially break if I create a queue with both
            // an order status reason filter AND another filter that is not
            // order type. That queue could be counted as "eligible" in this
            // logic even though it would technically filter out things.

            // Check order_status_reason_id match
            const statusReasonMatch = matchesSetFilterValue(
                queue.filter["order_activity.order_status_reason_id"],
                queueRoute.toOrderStatusReasonId
            );

            // If filter includes order_type, check that as well
            if (queue.filter["order_activity.order_type"] && queue.filter["order_activity.order_type"].properties?.value?.length > 0) {
                const orderActivityType = orderActivityTypes?.find(type => type.id === queueRoute.orderActivityTypeId);
                if (!orderActivityType) {
                    return false;
                }

                return statusReasonMatch && matchesSetFilterValue(
                    queue.filter["order_activity.order_type"],
                    orderActivityType.name
                );
            }

            // Otherwise, just match on status reason
            return statusReasonMatch;
        });
    }

    /**
     * Iterates over all queues of the given type and determines if the provided
     * orderStatusReasonId is included in the filter for any of the queues.
     *
     * @param {array} queues List of queues.
     * @param {number} orderStatusReasonId The order status reason ID to check.
     * @param {number} queueTypeTermId The type ID of the queue to filter by.
     * @return {boolean} True if orderStatusReasonId is included in any queue of
     * the given type, otherwise false.
     */
    static reasonIsInQueueType(queues, orderStatusReasonId, queueTypeTermId) {
        return queues.some(queue => {
            // Check if the queue type matches the provided queueTypeTermId
            if (queue.queueTypeTermId !== queueTypeTermId) {
                return false;
            }

            // Check if the queue has a valid filter for order_status_reason_id
            if (
                (
                    queue.table === "order_activity" ||
                    queue.table.startsWith("order_activity.")
                ) &&
                queue.filter &&
                queue.filter["order_activity.order_status_reason_id"]
            ) {
                return matchesSetFilterValue(
                    queue.filter["order_activity.order_status_reason_id"],
                    orderStatusReasonId
                );
            }

            return false;
        });
    }


}
